import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

// Renders post/page HTML and opens any clicked image in a full-screen lightbox.
// Phone: image fills the screen. Tablet & up: large but contained (~90% viewport).
export function PostContent({ html, className, testId }) {
  const ref = useRef(null);
  const [src, setSrc] = useState(null);

  // Render any mermaid diagrams found in the content (theme-aware, safe fallback).
  // Reads the live theme from the <html> class and re-renders when it changes,
  // so this component never re-renders on theme toggle (which would disturb content).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!el.querySelector('.mermaid-diagram[data-mermaid-src]')) return;

    let cancelled = false;

    const renderAll = async () => {
      const nodes = el.querySelectorAll('.mermaid-diagram[data-mermaid-src]');
      if (!nodes.length) return;
      const mermaid = (await import('mermaid')).default;
      if (cancelled) return;
      const isDark = document.documentElement.classList.contains('dark');
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'strict',
        theme: isDark ? 'dark' : 'default',
      });
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const encoded = node.getAttribute('data-mermaid-src') || '';
        let code = '';
        try {
          code = decodeURIComponent(escape(atob(encoded)));
        } catch (e) {
          code = encoded;
        }
        const id = `mmd-${Date.now()}-${i}-${Math.floor(Math.random() * 100000)}`;
        try {
          const { svg } = await mermaid.render(id, code);
          if (cancelled) return;
          node.innerHTML = svg;
        } catch (e) {
          if (cancelled) return;
          const escaped = code
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
          node.innerHTML =
            `<div class="mermaid-error"><p class="mermaid-error-msg">This diagram could not be rendered.</p>` +
            `<pre><code>${escaped}</code></pre></div>`;
        }
      }
    };

    renderAll();

    // Re-render diagrams when the light/dark theme changes
    let lastDark = document.documentElement.classList.contains('dark');
    const obs = new MutationObserver(() => {
      const nowDark = document.documentElement.classList.contains('dark');
      if (nowDark !== lastDark) {
        lastDark = nowDark;
        renderAll();
      }
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      cancelled = true;
      obs.disconnect();
    };
  }, [html]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onClick = (e) => {
      const img = e.target.closest('img');
      if (img && el.contains(img)) {
        e.preventDefault();
        // Open the full-size image (strip any ?w= responsive param)
        const raw = img.getAttribute('src') || img.src || '';
        setSrc(raw.split('?')[0] || img.src);
      }
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [html]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSrc(null); };
    if (src) {
      window.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [src]);

  return (
    <>
      <div
        ref={ref}
        className={className}
        data-testid={testId}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {src && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-0 sm:p-8 cursor-zoom-out animate-fade-in"
          onClick={() => setSrc(null)}
          data-testid="image-lightbox"
        >
          <button
            className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white/90 hover:bg-white/20 hover:text-white transition-colors"
            onClick={(e) => { e.stopPropagation(); setSrc(null); }}
            aria-label="Close image"
            data-testid="lightbox-close"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={src}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="w-auto h-auto max-w-full max-h-full sm:max-w-[90vw] sm:max-h-[90vh] object-contain sm:rounded-lg"
            data-testid="lightbox-image"
          />
        </div>
      )}
    </>
  );
}
